'use client';

import useSWR from 'swr';
import dayjs from 'dayjs';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useRef, useState } from 'react';
import { BadgeCardListSection, Container, TagFilter } from '@stereoagency/ui';
import flush from 'just-flush';
import { TaxonomyType, defaultLanguage } from '@stereoagency/constants';
import type { RouteResultType } from '@/app/(root)/api/data/event/route';
import { useClientUrl, useLoadMoreData } from '@/hooks';
import {
  eventAsProps,
  filterOptionsFromDocuments,
  findInKeyArray
} from '@/utils';

type Props = {
  enableTaxonomyFilter?: boolean;
  taxonomyCounts?: Record<string, number>;
};

const eventsFetcher = async (offset: number) => {
  const url = new URL('/api/data/event', window.location.href);
  url.searchParams.append('offset', String(offset));
  url.searchParams.append('after', dayjs().subtract(1, 'day').toISOString());
  const response = await fetch(url.toString());
  const data = await response.json();
  return data as RouteResultType;
};

export default function EventListingSection({
  enableTaxonomyFilter,
  taxonomyCounts
}: Props) {
  const { t, lang } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const url = useClientUrl();
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<string[]>(['all']);

  const { data, isLoading } = useSWR(
    () => [`/api/data/events`, offset] as const,
    async ([, key]): Promise<RouteResultType> => {
      const result = await eventsFetcher(key);
      return (data ?? []).concat(result);
    },
    {
      keepPreviousData: true,
      revalidateOnReconnect: false,
      revalidateOnFocus: false,
      revalidateIfStale: false,
    }
  );

  const moreKey = `${data?.length ?? 0}-${selected.join('/')}`;
  useLoadMoreData(containerRef, moreKey, () => {
    if (isLoading) throw new Error('already loading');
    setOffset((offset ?? 0) + 20);
  });

  const filteredEvents =
    data?.filter((event) => {
      if (selected.length === 1 && selected[0] === 'all') return true;
      if (!event.taxonomies?.length) return false;
      return event.taxonomies.some(
        (tax) => tax.slug?.current && selected.includes(tax.slug.current)
      );
    }) ?? [];

  const filters = filterOptionsFromDocuments(
    data ?? [],
    (doc) =>
      flush(
        doc.taxonomies?.map((tax) => {
          const name = tax.name
            ? findInKeyArray(tax.name, [lang, defaultLanguage.id])
            : null;
          if (
            !tax.slug?.current ||
            !name?.value ||
            tax.type !== TaxonomyType.EVENT_CATEGORY
          ) {
            return null;
          }
          return { label: name.value, value: tax.slug.current };
        }) ?? []
      ),
    taxonomyCounts
  );

  const filtersParam = url?.searchParams.get('filters');
  useEffect(() => {
    const filtersParamOptions = flush(
      (filtersParam?.split(',') ?? []).map((value) =>
        filters.find((entry) => entry.value === value)
      )
    );
    if (filtersParamOptions.length) {
      setSelected(filtersParamOptions.map((opt) => opt.value));
    }
  }, [filtersParam]);

  if (filteredEvents.length === 0) {
    return null;
  }

  return (
    <>
      {enableTaxonomyFilter && (
        <Container name={EventListingSection.name}>
          <TagFilter
            value={selected}
            options={[{ label: t('global:all'), value: 'all' }, ...filters]}
            accentColor="--colors-purple-700"
            onChange={setSelected}
          />
        </Container>
      )}
      <Container ref={containerRef} name={EventListingSection.name}>
        <BadgeCardListSection
          entries={flush(
            data?.map((event) => eventAsProps(event, lang, { t })) ?? []
          )}
        />
      </Container>
    </>
  );
}
